import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { useCreateSendWindow, useDeleteSendWindow } from '../api/queries';
import type { SendWindow } from '../api/types';
import { useToast } from './Toast';

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTES_PER_DAY = 24 * 60;
const PIXELS_PER_MINUTE = 1;
const SNAP_MINUTES = 15;
const MIN_BLOCK_MINUTES = 15;

const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** Montag 00:00 der Woche, in der `date` liegt (lokale Zeit). */
function startOfWeek(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const isoWeekday = (start.getDay() + 6) % 7; // 0 = Montag statt Sonntag
  start.setDate(start.getDate() - isoWeekday);
  return start;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function addMinutes(date: Date, minutes: number): Date {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() + minutes);
  return result;
}

function isSameDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function snapMinutes(minutes: number): number {
  return Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
}

function formatHourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

function formatMinutesAsTime(totalMinutes: number): string {
  const wrapped = totalMinutes % MINUTES_PER_DAY;
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

interface Segment {
  sendWindow: SendWindow;
  dayIndex: number;
  startMinute: number;
  endMinute: number;
  isPartial: boolean;
}

/**
 * Zerlegt jedes Zeitfenster in Tagesabschnitte innerhalb der angezeigten
 * Woche -- ein über Mitternacht laufendes Fenster (siehe `buildRange` in
 * SendWindowsSection) erzeugt so zwei Segmente, je eins pro Kalendertag.
 */
function segmentsForWeek(windows: SendWindow[], weekStart: Date): Segment[] {
  const weekEnd = addDays(weekStart, 7);
  const segments: Segment[] = [];

  for (const sendWindow of windows) {
    const start = new Date(sendWindow.startAt);
    const end = new Date(sendWindow.endAt);
    let cursor = start;

    while (cursor < end) {
      const dayStart = new Date(cursor);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = addDays(dayStart, 1);
      const segmentStart = cursor > dayStart ? cursor : dayStart;
      const segmentEnd = end < dayEnd ? end : dayEnd;

      if (dayStart >= weekStart && dayStart < weekEnd) {
        const dayIndex = Math.round((dayStart.getTime() - weekStart.getTime()) / DAY_MS);
        segments.push({
          sendWindow,
          dayIndex,
          startMinute: segmentStart.getHours() * 60 + segmentStart.getMinutes(),
          endMinute: segmentEnd.getTime() === dayEnd.getTime() ? MINUTES_PER_DAY : segmentEnd.getHours() * 60 + segmentEnd.getMinutes(),
          isPartial: start < dayStart || end > dayEnd,
        });
      }

      cursor = dayEnd;
    }
  }

  return segments;
}

interface DragState {
  dayIndex: number;
  startMinute: number;
  currentMinute: number;
}

/**
 * Wochenansicht, standardmäßig Montag bis Sonntag der aktuellen Woche --
 * über die Pfeile daneben aber auch zu anderen Wochen navigierbar. Zeigt
 * bestehende Abschickzeiten als Blöcke und erlaubt, neue direkt per Ziehen
 * anzulegen (ähnlich Google Kalender). Ergänzt das Formular in
 * SendWindowsSection, ersetzt es nicht: exaktes Bearbeiten bleibt dort.
 */
export function SendWindowsWeekCalendar({ windows }: { windows: SendWindow[] }) {
  const create = useCreateSendWindow();
  const remove = useDeleteSendWindow();
  const toast = useToast();

  const currentWeekStart = useRef(startOfWeek(new Date())).current;
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStart = addDays(currentWeekStart, weekOffset * 7);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const columnRefs = useRef<Array<HTMLDivElement | null>>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  useEffect(() => {
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const scroll = scrollRef.current;
    if (scroll) {
      scroll.scrollTop = Math.max(0, (nowMinutes - 120) * PIXELS_PER_MINUTE);
    }
    // Nur beim ersten Rendern scrollen, nicht bei jedem Minuten-Tick von `now`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minuteFromClientY = (dayIndex: number, clientY: number): number => {
    const column = columnRefs.current[dayIndex];
    if (!column) return 0;
    const rect = column.getBoundingClientRect();
    return clamp(snapMinutes((clientY - rect.top) / PIXELS_PER_MINUTE), 0, MINUTES_PER_DAY);
  };

  const handlePointerDown = (dayIndex: number, event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const minute = minuteFromClientY(dayIndex, event.clientY);
    const next: DragState = { dayIndex, startMinute: minute, currentMinute: minute };
    dragRef.current = next;
    setDrag(next);
  };

  useEffect(() => {
    if (!drag) return;

    const handleMove = (event: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;
      const minute = minuteFromClientY(current.dayIndex, event.clientY);
      if (minute === current.currentMinute) return;
      const updated = { ...current, currentMinute: minute };
      dragRef.current = updated;
      setDrag(updated);
    };

    const handleUp = () => {
      const finished = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!finished) return;

      const startMinute = Math.min(finished.startMinute, finished.currentMinute);
      const endMinute = Math.min(
        Math.max(Math.max(finished.startMinute, finished.currentMinute), startMinute + MIN_BLOCK_MINUTES),
        MINUTES_PER_DAY,
      );

      const dayStart = addDays(weekStart, finished.dayIndex);
      const startAt = addMinutes(dayStart, startMinute);
      const endAt = addMinutes(dayStart, endMinute);

      create.mutate(
        { startAt: startAt.toISOString(), endAt: endAt.toISOString() },
        { onError: (error) => toast.push('error', error.message) },
      );
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag !== null]);

  const confirmDeleteBlock = (sendWindow: SendWindow, event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!window.confirm('Diese Abschickzeit löschen?')) return;
    remove.mutate({ id: sendWindow.id }, { onError: (error) => toast.push('error', error.message) });
  };

  const segments = segmentsForWeek(windows, weekStart);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const weekEndInclusive = addDays(weekStart, 6);

  return (
    <div className="week-calendar">
      <div className="week-calendar-nav">
        <button type="button" className="btn btn-sm" onClick={() => setWeekOffset((offset) => offset - 1)}>
          ← Vorherige Woche
        </button>
        <div className="week-calendar-nav-label">
          {weekStart.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          {' – '}
          {weekEndInclusive.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          {weekOffset !== 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(0)}>
              Heute
            </button>
          )}
        </div>
        <button type="button" className="btn btn-sm" onClick={() => setWeekOffset((offset) => offset + 1)}>
          Nächste Woche →
        </button>
      </div>

      <div className="week-calendar-header-row">
        <div className="week-calendar-corner" />
        {WEEKDAY_LABELS.map((label, dayIndex) => {
          const date = addDays(weekStart, dayIndex);
          const isToday = isSameDate(date, now);
          return (
            <div key={label} className={`week-calendar-day-header${isToday ? ' is-today' : ''}`}>
              <span>{label}</span>
              <span className="week-calendar-day-date">
                {date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
              </span>
            </div>
          );
        })}
      </div>

      <div className="week-calendar-scroll" ref={scrollRef}>
        <div className="week-calendar-body-grid">
          <div className="week-calendar-gutter-col">
            {Array.from({ length: 24 }, (_, hour) => (
              <div key={hour} className="week-calendar-hour-label" style={{ top: hour * 60 * PIXELS_PER_MINUTE }}>
                {formatHourLabel(hour)}
              </div>
            ))}
          </div>

          {WEEKDAY_LABELS.map((label, dayIndex) => {
            const date = addDays(weekStart, dayIndex);
            const isToday = isSameDate(date, now);

            return (
              <div
                key={label}
                className="week-calendar-day-col"
                ref={(el) => {
                  columnRefs.current[dayIndex] = el;
                }}
                onPointerDown={(event) => handlePointerDown(dayIndex, event)}
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <div key={hour} className="week-calendar-hour-line" style={{ top: hour * 60 * PIXELS_PER_MINUTE }} />
                ))}

                {isToday && <div className="week-calendar-now-line" style={{ top: nowMinutes * PIXELS_PER_MINUTE }} />}

                {segments
                  .filter((segment) => segment.dayIndex === dayIndex)
                  .map((segment) => (
                    <button
                      type="button"
                      key={`${segment.sendWindow.id}-${segment.startMinute}`}
                      className="week-calendar-block"
                      style={{
                        top: segment.startMinute * PIXELS_PER_MINUTE,
                        height: Math.max((segment.endMinute - segment.startMinute) * PIXELS_PER_MINUTE, 16),
                      }}
                      title="Klicken zum Löschen"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => confirmDeleteBlock(segment.sendWindow, event)}
                    >
                      {formatMinutesAsTime(segment.startMinute)}–{formatMinutesAsTime(segment.endMinute)}
                      {segment.isPartial && ' ⋯'}
                    </button>
                  ))}

                {drag && drag.dayIndex === dayIndex && (
                  <div
                    className="week-calendar-drag-preview"
                    style={{
                      top: Math.min(drag.startMinute, drag.currentMinute) * PIXELS_PER_MINUTE,
                      height: Math.max(Math.abs(drag.currentMinute - drag.startMinute), MIN_BLOCK_MINUTES) * PIXELS_PER_MINUTE,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
